import { zodResolver } from '@hookform/resolvers/zod';
import { useIsMutating, useMutation } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import queryClient from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import i18n from '@/Config/app/i18n';
import {
  RemoteMcpDto,
  RemoteMcpTool,
  RemoteMcpUpdateDto,
} from '@/Domain/api/Action/dto/RemoteMCP.dto';
import { buildMetadataCaptureSchema } from '@/Domain/hooks/Action/ApiAction/useHttpRequestActionFormV2.hook';
import { useModalStore } from '@/Domain/store/modal.store';

/** Matches REMOTE_MCP_TOOL_MEMORY_CAPTURES_MAX_COUNT on the backend. */
export const MCP_TOOL_MEMORY_CAPTURE_LIMIT = 10;
export const MCP_TOOL_THINKING_MESSAGE_LIMIT = 200;

type RemoteMcpCaptureRowError = {
  toolIndex: number;
  captureIndex: number;
  field: 'json_path' | 'metadata_key';
  message: string;
};

const getRemoteMcpCaptureRowErrors = (data: unknown): RemoteMcpCaptureRowError[] => {
  const errors = (data as { errors?: Record<string, Array<{ message?: string }>> } | undefined)
    ?.errors;
  if (!errors) return [];

  const rows: RemoteMcpCaptureRowError[] = [];
  for (const [path, items] of Object.entries(errors)) {
    const match = path.match(
      /allowed_tools\.(\d+)\.save_to_memory\.(\d+)\.(json_path|metadata_key)$/,
    );
    const message = items?.[0]?.message;
    if (!match || !message) continue;
    rows.push({
      toolIndex: Number(match[1]),
      captureIndex: Number(match[2]),
      field: match[3] as RemoteMcpCaptureRowError['field'],
      message: message.replace(/^(Value error|Type error|Assertion error),\s*/i, ''),
    });
  }
  return rows;
};

const mcpToolSchema = () =>
  z.object({
    name: z.string(),
    is_active: z.boolean(),
    thinking_message: z
      .string()
      .max(MCP_TOOL_THINKING_MESSAGE_LIMIT, {
        message: i18n.t('actions_page:sheet.form.thinking_message.limit_reached', {
          limit: MCP_TOOL_THINKING_MESSAGE_LIMIT,
        }),
      })
      .nullish(),
    save_to_memory: buildMetadataCaptureSchema(MCP_TOOL_MEMORY_CAPTURE_LIMIT).nullish(),
  });

const remoteMcpActionSchema = (isActive: boolean) => {
  if (isActive) {
    return z.object({
      name: z.string().min(1),
      default_tool_is_active: z.boolean(),
      allowed_tools: z.array(mcpToolSchema()),
    });
  }
  return z.object({
    name: z.string(),
    default_tool_is_active: z.boolean(),
    allowed_tools: z.array(mcpToolSchema()),
  });
};

export type RemoteMcpForm = z.infer<ReturnType<typeof remoteMcpActionSchema>>;

const valuesMatch = (left: unknown, right: unknown): boolean =>
  JSON.stringify(left ?? null) === JSON.stringify(right ?? null);

const mergeToolDrafts = (
  previousTools: RemoteMcpTool[],
  nextTools: RemoteMcpTool[],
  draftTools: RemoteMcpForm['allowed_tools'],
): RemoteMcpForm['allowed_tools'] => {
  const previousByName = new Map(previousTools.map((tool) => [tool.name, tool]));
  const draftByName = new Map(draftTools.map((tool) => [tool.name, tool]));

  return nextTools.map((nextTool) => {
    const previousTool = previousByName.get(nextTool.name);
    const draftTool = draftByName.get(nextTool.name);
    if (!previousTool || !draftTool) return nextTool;

    return {
      ...nextTool,
      ...(draftTool.is_active !== previousTool.is_active ? { is_active: draftTool.is_active } : {}),
      ...(!valuesMatch(draftTool.thinking_message, previousTool.thinking_message)
        ? { thinking_message: draftTool.thinking_message }
        : {}),
      ...(!valuesMatch(draftTool.save_to_memory, previousTool.save_to_memory)
        ? { save_to_memory: draftTool.save_to_memory }
        : {}),
    };
  });
};

type RemoteMcpActionFormOptions = {
  onSuccess?: (remoteMcpAction: RemoteMcpDto) => void;
};

export const useRemoteMcpActionForm = (
  remoteMcpAction?: RemoteMcpDto,
  options?: RemoteMcpActionFormOptions,
) => {
  const { t } = useTranslation('remote_mcp');
  const { open, closeModal } = useModalStore();

  const form = useForm<RemoteMcpForm>({
    mode: 'onChange',
    defaultValues: {
      name: remoteMcpAction?.name || '',
      default_tool_is_active: remoteMcpAction?.default_tool_is_active ?? true,
      allowed_tools: remoteMcpAction?.allowed_tools || [],
    },
    resolver: zodResolver(remoteMcpActionSchema(!!remoteMcpAction?.is_active)),
  });
  const previousRemoteMcpActionRef = useRef<RemoteMcpDto | undefined>(remoteMcpAction);
  const submissionPendingRef = useRef(false);

  const defaultPatchRemoteMcpMutationOptions = queryClient.getMutationDefaults([
    QueryKey.actions.remoteMcp.update,
  ]);
  const patchRemoteMcpMutation = useMutation<RemoteMcpDto, unknown, RemoteMcpUpdateDto>({
    mutationKey: [QueryKey.actions.remoteMcp.update, remoteMcpAction?.id],
    ...defaultPatchRemoteMcpMutationOptions,
    onSuccess: (data, variables, onMutateResult, context) => {
      submissionPendingRef.current = false;
      defaultPatchRemoteMcpMutationOptions?.onSuccess?.(data, variables, onMutateResult, context);
      form.reset({
        name: data.name,
        default_tool_is_active: data.default_tool_is_active,
        allowed_tools: data.allowed_tools,
      });
      previousRemoteMcpActionRef.current = data;
      if (options?.onSuccess) options.onSuccess(data);
      else closeModal();
    },
    onError: (error, variables, onMutateResult, context) => {
      submissionPendingRef.current = false;
      defaultPatchRemoteMcpMutationOptions?.onError?.(error, variables, onMutateResult, context);
      const responseData = (error as { response?: { data?: unknown } })?.response?.data;
      for (const { toolIndex, captureIndex, field, message } of getRemoteMcpCaptureRowErrors(
        responseData,
      )) {
        form.setError(`allowed_tools.${toolIndex}.save_to_memory.${captureIndex}.${field}`, {
          type: 'server',
          message,
        });
      }
    },
  });
  const isConnectionPending =
    useIsMutating({ mutationKey: [QueryKey.actions.remoteMcp.connect] }) > 0;
  const isActionUpdatePending =
    useIsMutating({
      mutationKey: [QueryKey.actions.remoteMcp.update, remoteMcpAction?.id],
    }) > 0;
  const isSubmissionPending = (): boolean =>
    submissionPendingRef.current ||
    queryClient.isMutating({ mutationKey: [QueryKey.actions.remoteMcp.connect] }) > 0 ||
    (!!remoteMcpAction &&
      queryClient.isMutating({
        mutationKey: [QueryKey.actions.remoteMcp.update, remoteMcpAction.id],
      }) > 0);

  const handleSubmit = (data: RemoteMcpForm): void => {
    if (!remoteMcpAction || isSubmissionPending()) return;
    const toolsWithStoredMemory = new Set(
      remoteMcpAction.allowed_tools
        .filter((tool) => tool.save_to_memory?.length)
        .map((tool) => tool.name),
    );
    const updateData = {
      id: remoteMcpAction.id,
      name: data.name,
      default_tool_is_active: data.default_tool_is_active,
      allowed_tools: data.allowed_tools.map(({ save_to_memory, thinking_message, ...rest }) => {
        // A stored "" is a deliberate suppression server-side; only a cleared field (null) is
        // omitted so the derived default applies again.
        const tool = thinking_message != null ? { ...rest, thinking_message } : rest;
        return save_to_memory?.length
          ? {
              ...tool,
              save_to_memory: save_to_memory.map(({ json_path, metadata_key }) => ({
                json_path,
                metadata_key,
              })),
            }
          : toolsWithStoredMemory.has(tool.name)
            ? { ...tool, save_to_memory: [] }
            : tool;
      }),
    };
    submissionPendingRef.current = true;
    patchRemoteMcpMutation.mutate(updateData);
  };

  useEffect(() => {
    if (remoteMcpAction) {
      const nextValues: RemoteMcpForm = {
        name: remoteMcpAction.name,
        default_tool_is_active: remoteMcpAction.default_tool_is_active,
        allowed_tools: remoteMcpAction.allowed_tools,
      };
      const previousAction = previousRemoteMcpActionRef.current;
      const draftValues = form.getValues();

      form.reset(nextValues);
      if (previousAction && previousAction.id === remoteMcpAction.id) {
        if (draftValues.name !== previousAction.name) {
          form.setValue('name', draftValues.name, { shouldDirty: true });
        }
        if (draftValues.default_tool_is_active !== previousAction.default_tool_is_active) {
          form.setValue('default_tool_is_active', draftValues.default_tool_is_active, {
            shouldDirty: true,
          });
        }
        if (previousAction.remote_mcp_url === remoteMcpAction.remote_mcp_url) {
          const mergedTools = mergeToolDrafts(
            previousAction.allowed_tools,
            remoteMcpAction.allowed_tools,
            draftValues.allowed_tools,
          );
          if (!valuesMatch(mergedTools, remoteMcpAction.allowed_tools)) {
            form.setValue('allowed_tools', mergedTools, { shouldDirty: true });
          }
        }
      }
      previousRemoteMcpActionRef.current = remoteMcpAction;

      return;
    }

    form.reset({
      name: '',
      default_tool_is_active: true,
      allowed_tools: [],
    });
    previousRemoteMcpActionRef.current = undefined;
  }, [open, remoteMcpAction]);

  const onInvalid = () => {
    toast.error(t('toast.invalid_update_on_is_active'));
  };

  return {
    form,
    handleSubmit,
    onInvalid,
    isPending: isConnectionPending || isActionUpdatePending,
    isSubmissionPending,
  };
};
